import { ReactNode, SVGProps } from 'react'

import ChartBarIcon from '@heroicons/react/outline/ChartBarIcon'
import QuestionMarkCircleIcon from '@heroicons/react/outline/QuestionMarkCircleIcon'
import ShareIcon from '@heroicons/react/outline/ShareIcon'
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon'
import TemplateIcon from '@heroicons/react/outline/TemplateIcon'
import UsersIcon from '@heroicons/react/outline/UsersIcon'

import { List } from '@tribeplatform/react-ui-kit/Layout'

type SectionType = {
  title: string
  icon: SVGProps<SVGSVGElement>
  key: string
  modal?: string
  tabs?: Array<{
    title: string
    content: string | ReactNode
    actions?: Array<{
      title: string
      modal?: string
      action?: () => void
      link?: string
      href?: string
    }>
    tips?: Array<string>
  }>
}

export const sections: SectionType[] = [
  {
    title: 'Set your community up for engagement',
    icon: <UsersIcon />,
    key: 'engagement',
    tabs: [
      {
        title: 'Community Structure',
        content: (
          <>
            <p>Categorize your community into Collections, and then Spaces</p>
            <p>
              Collections are used to contain one or more Space. A Space among
              many things could be used for posting announcements, collecting
              feedback, sharing and discussing ideas, asking and responding to
              questions, and much much more
            </p>
          </>
        ),
        actions: [
          {
            title: 'Add a new Collection to your community',
            modal: 'collection',
          },
          {
            title: 'Add a new Space',
            modal: 'space',
          },
        ],
        tips: [
          'Keep in mind that you can move a space from one collection to another',
          'Make the space accessible to an exclusive set of members by changing the access settings of a space',
          'Want to reserve one of your spaces for Announcements or a similar purpose? If so, simply change the posting permissions of a space, to restrict members from posting content in that space',
        ],
      },
      {
        title: 'Moderation',
        content: (
          <p>
            Create moderation rules and get notified when there is a new item
            that needs to be reviewed
          </p>
        ),
        actions: [
          {
            title: 'Moderation Settings',
            link: '/manage/settings/moderation',
          },
        ],
      },
      {
        title: 'Mobilize your team',
        content: (
          <p>Community management is a team sport. Get your team onboard!</p>
        ),
        actions: [
          {
            title: 'Invite a colleague',
            modal: 'invitation',
          },
        ],
        tips: [
          'After your colleagues register as members, simply change their role from Member to either Admin or Moderator to give them access to the features that they would need to help you manage your community',
        ],
      },
    ],
  },
  {
    title: 'Customize the look and branding of your community',
    icon: <TemplateIcon />,
    key: 'customize',
    tabs: [
      {
        title: 'Logo',
        content: (
          <>
            <p>Replace this temporary logo with your own</p>
          </>
        ),
        actions: [{ title: 'Upload logo', link: '/manage/settings/network' }],
      },
      {
        title: 'Custom Domain',
        content: (
          <>
            <p>
              The default domain that was provided for your community can be
              replaced with your own domain or subdomian
            </p>
          </>
        ),
        actions: [
          { title: 'Add your domain', link: '/manage/settings/domain' },
        ],
      },
      {
        title: 'Appearance',
        content: (
          <>
            <p>
              User our customizer tool to enhance the look of your community
            </p>
          </>
        ),
        actions: [{ title: 'Open customizer', link: '/customizer' }],
        tips: [
          'Make it easy for your members to navigate to other relevant online resources from your community, by adding a top navigation bar and customizing it to fit your needs',
        ],
      },
    ],
  },
  {
    title: 'Configure community access',
    icon: <ShieldCheckIcon />,
    key: 'access',
    tabs: [
      {
        title: 'Community privacy',
        content: (
          <>
            <h4>Private vs. Non-Private Community</h4>
            <p>
              The privacy of your community may be set to either private or
              non-private. As a private community, only registered members will
              be able to access it and actively participate in it (e.g. sharing
              content, commenting, and engaging with other members). If a
              community is non-private, then anyone can access and read through
              it, however active participation in a non-private community is
              reserved for registered members only.
            </p>
            <h4>Who can become a registered member of your community?</h4>
            <p>
              You may allow anyone to join your community and become a
              registered member if that’s the right choice for your community.
              Alternatively, you can have more control over who can become a
              registered member by limiting the registration to invited
              individuals only.
            </p>
          </>
        ),
        actions: [
          {
            title: 'Review all privacy settings for your community',
            link: '/members',
          },
        ],
      },
      {
        title: 'Registration options',
        content: (
          <>
            <p>
              You can provide your community with three types of options to
              become a registered member
            </p>
            <List>
              <List.Item>Sign up with email and password</List.Item>
              <List.Item>
                Link an existing social account (e.g. Facebook, Google, or
                LinkedIn)
              </List.Item>
              <List.Item>
                Integration with one of the available SSO providers
              </List.Item>
            </List>
          </>
        ),
        actions: [{ title: 'Toggle registration options', link: '/members' }],
      },
      {
        title: 'Inviting members',
        content: (
          <p>
            Take the lead in the formation of your community’s member base, by
            inviting them to join your community. You may invite the intended
            members to join you community by either sharing an invitation link,
            or sending them a personalized invitation.
          </p>
        ),
        actions: [
          { title: 'Invite new members', modal: 'invitation' },
          { title: 'View all members', link: '/manage/people/members' },
        ],
        tips: [
          'Sending personalized invitations through our platform gives you the benefits of tracking the progress of each invitation, inviting several individuals at once, and the flexibility to customize the invitation message',
        ],
      },
    ],
  },
  {
    title: 'Track engagement',
    icon: <ChartBarIcon />,
    key: 'track',
    tabs: [
      {
        title: 'Track engagement',
        content: (
          <p>
            Get a comprehensive understanding of how engaged your community is,
            as well as what and who are the key drivers of that engagement
          </p>
        ),
        actions: [
          {
            title: 'Open the Analytics for your community',
            href: '/admin/network/analytics',
          },
        ],
      },
    ],
  },
  {
    title: 'Integrate your community into your ecosystem',
    icon: <ShareIcon />,
    key: 'integrate',
    tabs: [
      {
        title: 'Single Sign-On',
        content: <p>Provide your members with a seamless login experience</p>,
        actions: [
          {
            title: 'Learn about instrumenting the Single-Sign On option',
            link: '/members',
          },
        ],
      },
      {
        title: 'Embedding your community',
        content: (
          <p>
            Want to embed your community on your website, an app or any other
            digital property?
          </p>
        ),
        actions: [
          { title: 'Learn about embedding your community', link: '/members' },
        ],
      },
      {
        title: 'Apps',
        content: (
          <p>
            We built several apps to help you further customize your community
            and integrate it with 3rd party tools
          </p>
        ),
        actions: [
          {
            title: 'Check out our Apps',
            link: '/members',
          },
        ],
        tips: [
          'Our most popular app, allows you to use Zapier to integrate your community with thousands of 3rd party tools and platforms',
        ],
      },
      {
        title: 'Build your own app',
        content: (
          <>
            <h4>
              Did you know that you can add your own app to your community?
            </h4>
            <p>
              That&apos;s right, you can get access to our robust API to build
              your own app for your community
            </p>
          </>
        ),
        actions: [{ title: 'Visit developer portal', link: '/manage/apps' }],
      },
    ],
  },
  {
    title: 'Get help when you need it',
    icon: <QuestionMarkCircleIcon />,
    key: 'support',
    modal: 'support',
  },
]
